/**
 * Exchange / returns-UI enum cohort previously defined in
 * `redo/model/src/team.ts`. Lives in return/core/common so leaf packages can
 * reference them without depending on redo/model.
 *
 * The Mongo-persisted enums here (ExchangesSelection, ShopSiteNavigationMode,
 * VariantPickerType, NonInstantExchangeOrderProvision, DiscountDistributionMethod,
 * OutOfStockAdvanced, ExchangeType, AdvancedExchanges) are registered via
 * `typedEnum(...)` in `redo/backend/db/schema/src/Team.ts`; keep those in sync.
 */

export enum ExchangesSelection {
  RETURN_APP = "return_app",
  STORE_WEBSITE = "store_website",
}

export enum ShopSiteNavigationMode {
  /** New window */
  NEW_TAB = "new_tab",
  /** Same window */
  SAME_TAB = "same_tab",
}

export enum VariantPickerType {
  AUTO_SELECT = "auto-select",
  MANUAL_SELECT = "manual-select",
}

export enum NonInstantExchangeOrderProvision {
  ALL_PRODUCTS_PROCESSED = "all-products-processed",
  EXCHANGE_PRODUCTS_PROCESSED = "exchange-products-processed",
  RETURN_APPROVED = "return-approved",
}

export enum DiscountDistributionMethod {
  FAMILY = "DistributeAcrossFamily",
  ORDER = "DistributeAcrossOrder",
  LINE_ITEM = "LineItem",
}

export enum OutOfStockAdvanced {
  CANCEL = "cancel",
  CONTINUE = "continue",
  MANUAL = "manual",
  OVERSELL = "oversell",
}

export enum LineItemDetailOption {
  VariantInventory = "variantInventory",
  ViewInventoryDetails = "viewInventoryDetails",
}

export interface MetafieldDisplayConfig {
  /** The full metafield key in "namespace.key" format */
  key: string;
  displayName?: string;
}

export type InstantExchangeHoldKind = "none" | "one_dollar" | "full_cost";

export enum ExchangeType {
  VARIANT = "variant",
  ADVANCED = "advanced",
}

export enum AdvancedExchanges {
  ENABLED = "all",
  /** Limits advanced exchanges to items with value less than or equal to the exchange value */
  LESS_THAN_EQUAL = "less_than_equal",
  DISABLED = "variant_only",
}

export enum ExchangeFinancialStrategy {
  NONE = "none", // Fallback to the old non-zero value exchange strategy. Delete once all teams are migrated.
  UPSELL_VALUE = "upsell_value",
  FULL_PRICE_DISCOUNTED_TO_UPSELL_VALUE = "full_price_discount_to_upsell_value",
  FULL_PRICE = "full_price",
}

export const excessExchangeValueMethods = ["store_credit", "refund"] as const;
export type ExcessExchangeValueMethod =
  (typeof excessExchangeValueMethods)[number];

export const excessExchangeValueOptions = [
  ...excessExchangeValueMethods,
  "customer_choose",
] as const;

export type ExcessExchangeValueOption =
  (typeof excessExchangeValueOptions)[number];

export const leftoverExchangeValueSelectionSources = [
  "shopper",
  "forced",
] as const;
export type LeftoverExchangeValueSelectionSource =
  (typeof leftoverExchangeValueSelectionSources)[number];
